import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';

import {
  getActiveBookCategoriesByLibrary,
  addBook,
  addBookCopy,
  getErrorMessage
} from '../../api/api';
import '../BookList.css';
import './AddBook.css';
import '../lib-page-header.css';
import FilterSelect from '../components/FilterSelect';

const AddBook = () => {
  const navigate = useNavigate();
  const sectionBase = useSectionBase();

  const libraryId = useActiveLibraryId();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    library_id: libraryId || '',
    category_id: '',
    title: '',
    subtitle: '',
    author_name: '',
    publisher_name: '',
    isbn: '',
    edition: '',
    language: '',
    publish_year: '',
    description: '',
    cover_image: '',

    late_fine_per_day_override: '',
    lost_charge_override: '',
    damage_charge_override: '',
    status: 'active',

    num_copies: 1,
    accession_prefix: '',
    barcode_prefix: '',

    catalogue_no: '',
    room_no: '',
    almirah_no: '',
    rack_no: '',
    shelf_no: '',
    row_no: '',
    purchase_date: '',
    purchase_price: '',
    vendor_name: '',
    condition_status: 'good',
    is_reference_only: 0,
    remarks: ''
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      library_id: libraryId || ''
    }));
  }, [libraryId]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        if (!libraryId) return;

        const data = await getActiveBookCategoriesByLibrary(libraryId);
        setCategories(data || []);
      } catch (error) {
        console.error('Fetch categories error:', error);
        Swal.fire('Error', getErrorMessage(error), 'error');
      }
    };

    fetchCategories();
  }, [libraryId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const validateForm = () => {
    if (!formData.library_id) {
      Swal.fire('Error', 'Library ID not found', 'error');
      return false;
    }

    if (!formData.category_id) {
      Swal.fire('Error', 'Please select book category', 'error');
      return false;
    }

    if (!formData.title.trim()) {
      Swal.fire('Error', 'Book title is required', 'error');
      return false;
    }

    if (Number(formData.num_copies || 0) <= 0) {
      Swal.fire('Error', 'Number of copies must be greater than 0', 'error');
      return false;
    }

    return true;
  };

  const generateCode = (prefix, bookId, index) => {
    const base =
      prefix ||
      formData.isbn ||
      `BK-${bookId}-${Date.now()}`;

    return `${base}-${index}`;
  };

  const resetForm = () => {
    setFormData({
      library_id: libraryId || '',
      category_id: '',
      title: '',
      subtitle: '',
      author_name: '',
      publisher_name: '',
      isbn: '',
      edition: '',
      language: '',
      publish_year: '',
      description: '',
      cover_image: '',

      late_fine_per_day_override: '',
      lost_charge_override: '',
      damage_charge_override: '',
      status: 'active',

      num_copies: 1,
      accession_prefix: '',
      barcode_prefix: '',

      catalogue_no: '',
      room_no: '',
      almirah_no: '',
      rack_no: '',
      shelf_no: '',
      row_no: '',
      purchase_date: '',
      purchase_price: '',
      vendor_name: '',
      condition_status: 'good',
      is_reference_only: 0,
      remarks: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const bookPayload = {
        library_id: formData.library_id,
        category_id: formData.category_id,
        title: formData.title.trim(),
        subtitle: formData.subtitle || null,
        author_name: formData.author_name || null,
        publisher_name: formData.publisher_name || null,
        isbn: formData.isbn || null,
        edition: formData.edition || null,
        language: formData.language || null,
        publish_year: formData.publish_year || null,
        description: formData.description || null,
        cover_image: formData.cover_image || null,
        late_fine_per_day_override: formData.late_fine_per_day_override || null,
        lost_charge_override: formData.lost_charge_override || null,
        damage_charge_override: formData.damage_charge_override || null,
        status: formData.status || 'active'
      };

      const bookResult = await addBook(bookPayload);

      const bookId =
        bookResult?.insertId ||
        bookResult?.id ||
        bookResult?.data?.insertId ||
        bookResult?.data?.id;

      if (!bookId) {
        throw new Error('Book created but book id not received');
      }

      const totalCopies = Number(formData.num_copies || 1);

      for (let i = 1; i <= totalCopies; i++) {
        const accessionNo = generateCode(formData.accession_prefix, bookId, i);
        const barcode = generateCode(formData.barcode_prefix || formData.accession_prefix, bookId, i);

        const copyPayload = {
          book_id: bookId,
          accession_no: accessionNo,
          barcode: barcode,
          catalogue_no: formData.catalogue_no || null,
          copy_number: i,

          room_no: formData.room_no || null,
          almirah_no: formData.almirah_no || null,
          rack_no: formData.rack_no || null,
          shelf_no: formData.shelf_no || null,
          row_no: formData.row_no || null,

          purchase_date: formData.purchase_date || null,
          purchase_price: formData.purchase_price || null,
          vendor_name: formData.vendor_name || null,

          condition_status: formData.condition_status || 'good',
          availability_status: 'available',
          is_reference_only: formData.is_reference_only ? 1 : 0,
          is_discarded: 0,
          discarded_at: null,
          discard_reason: null,
          remarks: formData.remarks || null
        };

        await addBookCopy(copyPayload);
      }

      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Book and copies added successfully'
      });

      resetForm();
      navigate(`${sectionBase}/books`);
    } catch (error) {
      console.error('Add book error:', error);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: getErrorMessage(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const copyCount = Math.max(0, Number(formData.num_copies) || 0);

  return (
    <div className="ab-page">

      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-book" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Add Book</h3>
            <div className="lib-page-header__subtitle">Create a catalogue record and generate its physical copies</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button type="button" className="ab-btn ab-btn-ghost" onClick={() => navigate(`${sectionBase}/books`)}>
            <i className="ti ti-arrow-left" /> Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="ab-main">

            {/* Book details */}
            <section className="ab-section">
              <div className="ab-section-head">
                <span className="ab-ico ab-ico-indigo"><i className="ti ti-book-2" /></span>
                <div>
                  <h4>Book Details</h4>
                  <small>Catalogue information for this title</small>
                </div>
                <span className="ab-section-hint"><span className="ab-req">*</span> required</span>
              </div>

              <div className="ab-section-body">
                <div className="ab-grid">
                  <div className="ab-field">
                    <label>Category <span className="ab-req">*</span></label>
                    <FilterSelect
                      className="lib-select-block"
                      triggerClassName="ab-input"
                      placeholder="Select category"
                      value={formData.category_id}
                      options={[
                        { value: '', label: 'Select category' },
                        ...categories.map((category) => ({ value: category.id, label: category.name })),
                      ]}
                      onChange={(v) => handleChange({ target: { name: 'category_id', value: v } })}
                    />
                  </div>

                  <div className="ab-field">
                    <label>Book Title <span className="ab-req">*</span></label>
                    <input type="text" name="title" className="ab-input" placeholder="Enter book title" value={formData.title} onChange={handleChange} required />
                  </div>

                  <div className="ab-field">
                    <label>Subtitle</label>
                    <input type="text" name="subtitle" className="ab-input" placeholder="Enter subtitle" value={formData.subtitle} onChange={handleChange} />
                  </div>

                  <div className="ab-field">
                    <label>Author</label>
                    <input type="text" name="author_name" className="ab-input" placeholder="Enter author name" value={formData.author_name} onChange={handleChange} />
                  </div>

                  <div className="ab-field">
                    <label>Publisher</label>
                    <input type="text" name="publisher_name" className="ab-input" placeholder="Enter publisher name" value={formData.publisher_name} onChange={handleChange} />
                  </div>

                  <div className="ab-field">
                    <label>ISBN</label>
                    <input type="text" name="isbn" className="ab-input" placeholder="Enter ISBN" value={formData.isbn} onChange={handleChange} />
                  </div>

                  <div className="ab-field">
                    <label>Edition</label>
                    <input type="text" name="edition" className="ab-input" placeholder="e.g. 2nd" value={formData.edition} onChange={handleChange} />
                  </div>

                  <div className="ab-field">
                    <label>Language</label>
                    <input type="text" name="language" className="ab-input" placeholder="e.g. English" value={formData.language} onChange={handleChange} />
                  </div>

                  <div className="ab-field">
                    <label>Publish Year</label>
                    <input type="number" name="publish_year" className="ab-input" placeholder="e.g. 2024" value={formData.publish_year} onChange={handleChange} />
                  </div>

                  <div className="ab-field">
                    <label>Status</label>
                    <FilterSelect
                      className="lib-select-block"
                      triggerClassName="ab-input"
                      value={formData.status}
                      options={[
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' },
                      ]}
                      onChange={(v) => handleChange({ target: { name: 'status', value: v } })}
                    />
                  </div>

                  <div className="ab-field ab-full">
                    <label>Description</label>
                    <textarea name="description" rows="3" className="ab-input" placeholder="Short summary, contents or notes about this title" value={formData.description} onChange={handleChange} />
                  </div>
                </div>
              </div>
            </section>

            {/* Copies & identification */}
            <section className="ab-section">
              <div className="ab-section-head">
                <span className="ab-ico ab-ico-blue"><i className="ti ti-stack-2" /></span>
                <div>
                  <h4>Copies &amp; Identification</h4>
                  <small>Each copy gets a unique accession number and barcode</small>
                </div>
              </div>

              <div className="ab-section-body">
                <div className="ab-grid">
                  <div className="ab-field">
                    <label>Number of Copies <span className="ab-req">*</span></label>
                    <input type="number" name="num_copies" className="ab-input" min="1" value={formData.num_copies} onChange={handleChange} required />
                  </div>

                  <div className="ab-field">
                    <label>Accession Prefix</label>
                    <input type="text" name="accession_prefix" className="ab-input" placeholder="Example: ACC-2026" value={formData.accession_prefix} onChange={handleChange} />
                  </div>

                  <div className="ab-field">
                    <label>Barcode Prefix</label>
                    <input type="text" name="barcode_prefix" className="ab-input" placeholder="Example: BAR-2026" value={formData.barcode_prefix} onChange={handleChange} />
                  </div>

                  <div className="ab-field">
                    <label>Catalogue No</label>
                    <input type="text" name="catalogue_no" className="ab-input" placeholder="Dewey / call number" value={formData.catalogue_no} onChange={handleChange} />
                  </div>

                  <div className="ab-field">
                    <label>Condition</label>
                    <FilterSelect
                      className="lib-select-block"
                      triggerClassName="ab-input"
                      value={formData.condition_status}
                      options={[
                        { value: 'good', label: 'Good' },
                        { value: 'damaged', label: 'Damaged' },
                        { value: 'lost', label: 'Lost' },
                      ]}
                      onChange={(v) => handleChange({ target: { name: 'condition_status', value: v } })}
                    />
                  </div>

                  <div className="ab-field">
                    <label>Reference Only</label>
                    <label className="ab-check" htmlFor="isReferenceOnly">
                      <input type="checkbox" name="is_reference_only" checked={Boolean(formData.is_reference_only)} onChange={handleChange} id="isReferenceOnly" />
                      <span>In-library use only (not issuable)</span>
                    </label>
                  </div>
                </div>
              </div>
            </section>

            {/* Shelving location */}
            <section className="ab-section">
              <div className="ab-section-head">
                <span className="ab-ico ab-ico-amber"><i className="ti ti-map-pin" /></span>
                <div>
                  <h4>Shelving Location</h4>
                  <small>Where these copies are physically stored</small>
                </div>
              </div>

              <div className="ab-section-body">
                <div className="ab-grid">
                  <div className="ab-field">
                    <label>Room No</label>
                    <input type="text" name="room_no" className="ab-input" value={formData.room_no} onChange={handleChange} />
                  </div>
                  <div className="ab-field">
                    <label>Almirah No</label>
                    <input type="text" name="almirah_no" className="ab-input" value={formData.almirah_no} onChange={handleChange} />
                  </div>
                  <div className="ab-field">
                    <label>Rack No</label>
                    <input type="text" name="rack_no" className="ab-input" value={formData.rack_no} onChange={handleChange} />
                  </div>
                  <div className="ab-field">
                    <label>Shelf No</label>
                    <input type="text" name="shelf_no" className="ab-input" value={formData.shelf_no} onChange={handleChange} />
                  </div>
                  <div className="ab-field">
                    <label>Row No</label>
                    <input type="text" name="row_no" className="ab-input" value={formData.row_no} onChange={handleChange} />
                  </div>
                </div>
              </div>
            </section>

            {/* Acquisition */}
            <section className="ab-section">
              <div className="ab-section-head">
                <span className="ab-ico ab-ico-green"><i className="ti ti-shopping-cart" /></span>
                <div>
                  <h4>Acquisition</h4>
                  <small>Purchase and supplier details</small>
                </div>
              </div>

              <div className="ab-section-body">
                <div className="ab-grid">
                  <div className="ab-field">
                    <label>Purchase Date</label>
                    <input type="date" name="purchase_date" className="ab-input" value={formData.purchase_date} onChange={handleChange} />
                  </div>
                  <div className="ab-field">
                    <label>Purchase Price</label>
                    <input type="number" name="purchase_price" className="ab-input" placeholder="0.00" value={formData.purchase_price} onChange={handleChange} />
                  </div>
                  <div className="ab-field">
                    <label>Vendor Name</label>
                    <input type="text" name="vendor_name" className="ab-input" placeholder="Supplier / bookseller" value={formData.vendor_name} onChange={handleChange} />
                  </div>
                  <div className="ab-field ab-full">
                    <label>Remarks</label>
                    <textarea name="remarks" rows="2" className="ab-input" placeholder="Any internal notes for these copies" value={formData.remarks} onChange={handleChange} />
                  </div>
                </div>
              </div>
            </section>

        </div>

        {/* ── Sticky action bar ── */}
        <div className="ab-bar">
          <span className="ab-bar-summary">
            <i className="ti ti-stack-2" />
            {copyCount > 0 ? (
              <span><b>{copyCount}</b> {copyCount === 1 ? 'copy' : 'copies'} will be created</span>
            ) : (
              <span>Set a quantity to create copies</span>
            )}
          </span>

          <button type="button" className="ab-btn ab-btn-ghost" onClick={() => navigate(`${sectionBase}/books`)}>
            Cancel
          </button>

          <button type="submit" className="ab-btn ab-btn-primary" disabled={loading}>
            {loading ? (
              <><i className="ti ti-loader-2 ab-spin" /> Saving…</>
            ) : (
              <><i className="ti ti-device-floppy" /> Save Book</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBook;